// TopBar "Game" menu — new match, save, load, back to the main menu.
// Entries whose handler is absent (e.g. save/load in multiplayer) are hidden.

import { Button } from '@aod/ui/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@aod/ui/components/ui/dropdown-menu';
import { Download, House, Menu, RotateCcw, Upload } from 'lucide-react';
import { useT } from '../../i18n/index.js';
import { appDom } from '../../lib/appDom.js';

interface GameMenuProps {
  readonly onNewMatch?: () => void;
  readonly onSave?: () => void;
  readonly onLoad?: () => void;
  readonly onReturnToMenu?: () => void;
}

export function GameMenu({ onNewMatch, onSave, onLoad, onReturnToMenu }: GameMenuProps) {
  const t = useT();
  const hasFileActions = Boolean(onSave || onLoad);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          data-testid="menu-game"
          {...appDom({
            id: 'menu-game',
            type: 'Dropdown',
            role: 'button',
            scope: 'header',
            label: 'Game menu',
          })}
          aria-label={t('menu.game')}
          className="h-8 px-2 lg:px-2.5"
        >
          <Menu className="size-4" />
          <span className="hidden lg:inline">{t('menu.game')}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="w-56"
        data-testid="menu-game-content"
        {...appDom({ id: 'menu-game-content', type: 'Dropdown', scope: 'menu' })}
      >
        {onNewMatch && (
          <DropdownMenuItem
            data-testid="menu-game-new"
            {...appDom({ id: 'menu-game-new', role: 'button', scope: 'menu' })}
            onSelect={onNewMatch}
          >
            <RotateCcw />
            {t('menu.game.newMatch')}
          </DropdownMenuItem>
        )}
        {onSave && (
          <DropdownMenuItem
            data-testid="menu-game-save"
            {...appDom({ id: 'menu-game-save', role: 'button', scope: 'menu' })}
            onSelect={onSave}
          >
            <Download />
            {t('menu.game.save')}
          </DropdownMenuItem>
        )}
        {onLoad && (
          <DropdownMenuItem
            data-testid="menu-game-load"
            {...appDom({ id: 'menu-game-load', role: 'button', scope: 'menu' })}
            onSelect={onLoad}
          >
            <Upload />
            {t('menu.game.load')}
          </DropdownMenuItem>
        )}
        {onReturnToMenu && (
          <>
            {(onNewMatch || hasFileActions) && <DropdownMenuSeparator />}
            <DropdownMenuItem
              data-testid="menu-game-main-menu"
              {...appDom({ id: 'menu-game-main-menu', role: 'button', scope: 'menu' })}
              onSelect={onReturnToMenu}
            >
              <House />
              {t('menu.game.mainMenu')}
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
